import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Bot, Check, Copy, Key, Plus, ShieldAlert, Trash2, Wrench } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { usePOS } from "@/context/POSContext";
import {
  createApiToken,
  listApiTokens,
  listMcpTools,
  revokeApiToken,
} from "@/lib/integrationsApi";

const reasonOf = err =>
  err?.response?.data?.detail || err?.message || "Operation failed";

const formatDate = iso => {
  if (!iso) return "Never";
  try {
    return new Date(iso).toLocaleString([], {
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
};

export default function AiAssistantPanel() {
  const { token, user } = usePOS();
  const isAdmin = user?.role === "admin";

  const [tokens, setTokens] = useState([]);
  const [tools, setTools] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Modal / create states
  const [creating, setCreating] = useState(false);
  const [tokenName, setTokenName] = useState("");
  const [allowWrite, setAllowWrite] = useState(false);
  const [saving, setSaving] = useState(false);

  // Created token display
  const [newlyCreated, setNewlyCreated] = useState(null);
  const [copied, setCopied] = useState(false);

  // Tools drawer state
  const [showTools, setShowTools] = useState(false);

  const load = useCallback(async () => {
    if (!isAdmin) return;
    try {
      setLoading(true);
      const [tokenList, toolData] = await Promise.all([
        listApiTokens(token),
        listMcpTools(token).catch(() => ({ tools: [] })),
      ]);
      setTokens(tokenList || []);
      setTools(toolData?.tools || []);
      setError(null);
    } catch (err) {
      setError(reasonOf(err));
    } finally {
      setLoading(false);
    }
  }, [token, isAdmin]);

  useEffect(() => {
    load();
  }, [load]);

  const handleCreate = async e => {
    e.preventDefault();
    const cleanName = tokenName.trim();
    if (!cleanName) {
      toast.error("Please provide a name for this assistant token");
      return;
    }

    const scopes = ["read"];
    if (allowWrite) scopes.push("write");

    setSaving(true);
    try {
      const created = await createApiToken({ name: cleanName, scopes }, token);
      setNewlyCreated(created);
      setTokenName("");
      setAllowWrite(false);
      setCreating(false);
      toast.success("API Token generated successfully");
      await load();
    } catch (err) {
      toast.error(`Could not generate token: ${reasonOf(err)}`);
    } finally {
      setSaving(false);
    }
  };

  const handleRevoke = async (tok) => {
    if (!window.confirm(`Revoke access for "${tok.name}" immediately? Any AI assistant using this token will stop working.`)) {
      return;
    }
    try {
      await revokeApiToken(tok.id, token);
      toast.success(`Revoked token "${tok.name}"`);
      await load();
    } catch (err) {
      toast.error(`Could not revoke token: ${reasonOf(err)}`);
    }
  };

  const handleCopy = (text) => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    toast.success("Token copied to clipboard");
    setTimeout(() => setCopied(false), 2500);
  };

  if (!isAdmin) return null;

  return (
    <section className="rounded-md border bg-card p-3 space-y-4" data-testid="ai-assistant-panel">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 border-b pb-3">
        <div className="space-y-1">
          <div className="flex items-center gap-2">
            <Bot className="h-5 w-5 text-action" />
            <h3 className="text-body font-bold text-foreground">AI Assistant (MCP Server)</h3>
            <span className="rounded bg-muted px-1.5 py-0.5 text-micro font-medium text-muted-foreground uppercase tracking-wide">
              Brand Bible §67
            </span>
          </div>
          <p className="text-meta text-muted-foreground max-w-2xl">
            Allow an AI assistant (Claude Desktop, Cursor, Antigravity) to query sales,
            reports, inventory, and tables over your local network. Off by default until you generate a token.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setShowTools(!showTools)}
            className="text-meta"
          >
            <Wrench className="h-3.5 w-3.5 mr-1" />
            {showTools ? "Hide Tools" : `View Tools (${tools.length})`}
          </Button>
          <Button
            size="sm"
            onClick={() => setCreating(true)}
            disabled={creating}
            className="bg-action text-action-foreground hover:bg-action/90 text-meta"
            data-testid="generate-token-btn"
          >
            <Plus className="h-3.5 w-3.5 mr-1" />
            Generate Token
          </Button>
        </div>
      </div>

      {/* Brand Bible §67 Security Notice */}
      <div className="rounded border border-primary/20 bg-primary/5 p-3 text-meta text-muted-foreground space-y-1">
        <div className="font-semibold text-foreground flex items-center gap-1.5">
          <Key className="h-4 w-4 text-primary" />
          Local Network & Privacy Guarantee
        </div>
        <p>
          RestoYaar POS sends nothing to external servers. Your AI client communicates directly with
          this POS server over your local Wi-Fi / LAN. Every tool call is logged in the POS audit trail
          under your name. Writing is locked unless explicitly enabled per token, and core ordering/billing
          can never be modified by the assistant.
        </p>
      </div>

      {/* Secret Token Reveal Dialog (Shown ONCE upon creation) */}
      {newlyCreated && (
        <div className="rounded-md border border-amber-500/40 bg-amber-500/10 p-4 space-y-3" data-testid="token-reveal-banner">
          <div className="flex items-center gap-2 text-amber-700 dark:text-amber-400 font-semibold text-body">
            <ShieldAlert className="h-5 w-5" />
            Save your API Token Now
          </div>
          <p className="text-meta text-muted-foreground">
            This token is displayed <strong>only once</strong> and is stored hashed. If you lose it,
            you will need to revoke it and generate a new one.
          </p>
          <div className="flex items-center gap-2">
            <code className="flex-1 font-mono text-meta bg-background border px-3 py-2 rounded select-all break-all text-foreground">
              {newlyCreated.token}
            </code>
            <Button
              variant="outline"
              size="sm"
              onClick={() => handleCopy(newlyCreated.token)}
              className="shrink-0"
              data-testid="copy-token-btn"
            >
              {copied ? <Check className="h-4 w-4 text-action" /> : <Copy className="h-4 w-4" />}
              <span className="ml-1.5">{copied ? "Copied" : "Copy"}</span>
            </Button>
          </div>
          <div className="text-micro text-muted-foreground pt-1 flex justify-between items-center">
            <span>Name: <strong>{newlyCreated.name}</strong> · Scopes: <strong>{newlyCreated.scopes.join(", ")}</strong></span>
            <Button variant="ghost" size="sm" onClick={() => setNewlyCreated(null)}>
              Done
            </Button>
          </div>
        </div>
      )}

      {/* Create Token Inline Form */}
      {creating && (
        <form onSubmit={handleCreate} className="rounded-md border bg-muted/40 p-3 space-y-3" data-testid="create-token-form">
          <div className="font-semibold text-body">New Assistant Token</div>
          <div className="grid gap-3 sm:grid-cols-2">
            <div>
              <label className="text-meta font-medium block mb-1">Token Name / Client Device</label>
              <Input
                placeholder="e.g. Kartik's Laptop, Kitchen Assistant"
                value={tokenName}
                onChange={e => setTokenName(e.target.value)}
                autoFocus
                required
                className="h-8 text-meta"
                data-testid="token-name-input"
              />
            </div>
            <div className="space-y-1">
              <label className="text-meta font-medium block mb-1">Permissions & Scopes</label>
              <div className="flex items-center justify-between border rounded p-2 bg-background">
                <div className="text-meta">
                  <span className="font-medium">Write Scope</span>
                  <p className="text-micro text-muted-foreground">Allow drafting purchase bills & starting stock counts</p>
                </div>
                <Switch
                  checked={allowWrite}
                  onCheckedChange={setAllowWrite}
                  data-testid="write-scope-switch"
                />
              </div>
            </div>
          </div>
          <div className="flex items-center justify-end gap-2 pt-1 border-t">
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => { setCreating(false); setTokenName(""); }}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              size="sm"
              disabled={saving || !tokenName.trim()}
              className="bg-action text-action-foreground hover:bg-action/90"
              data-testid="submit-token-btn"
            >
              {saving ? "Generating…" : "Create & Reveal Token"}
            </Button>
          </div>
        </form>
      )}

      {/* Tools Reference Drawer */}
      {showTools && (
        <div className="rounded-md border bg-muted/20 p-3 space-y-2" data-testid="mcp-tools-drawer">
          <div className="flex items-center justify-between font-semibold text-body">
            <span>Supported MCP Tools</span>
            <span className="text-micro text-muted-foreground">Total: {tools.length}</span>
          </div>
          <div className="grid gap-2 sm:grid-cols-2">
            {tools.map(t => (
              <div key={t.name} className="border rounded bg-card p-2 text-meta space-y-1">
                <div className="flex items-center justify-between">
                  <span className="font-mono font-semibold text-primary">{t.name}</span>
                  <span className={`px-1.5 py-0.2 rounded text-micro uppercase font-medium ${
                    t.scope === "write" ? "bg-amber-100 text-amber-800 dark:bg-amber-900 dark:text-amber-200" : "bg-muted text-muted-foreground"
                  }`}>
                    {t.scope}
                  </span>
                </div>
                <p className="text-micro text-muted-foreground leading-snug">{t.description}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Tokens List Table */}
      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <h4 className="text-body font-semibold">Active & Revoked Tokens</h4>
          <span className="text-micro text-muted-foreground">{tokens.length} token{tokens.length === 1 ? "" : "s"}</span>
        </div>

        {loading ? (
          <p className="text-meta text-muted-foreground py-4 text-center">Loading tokens…</p>
        ) : error ? (
          <p className="text-meta text-destructive py-4 text-center">{error}</p>
        ) : tokens.length === 0 ? (
          <div className="rounded border border-dashed p-6 text-center text-muted-foreground text-meta space-y-1">
            <Bot className="h-8 w-8 mx-auto opacity-30" />
            <div className="font-medium text-foreground">No API tokens created yet</div>
            <p>Click "Generate Token" above to connect Claude Desktop, Cursor, or another MCP client.</p>
          </div>
        ) : (
          <div className="rounded-md border overflow-x-auto">
            <table className="w-full text-meta" data-testid="tokens-table">
              <thead className="bg-muted/50 border-b text-left text-muted-foreground text-micro uppercase">
                <tr>
                  <th className="py-2 px-3">Token Name</th>
                  <th className="py-2 px-3">Prefix</th>
                  <th className="py-2 px-3">Scopes</th>
                  <th className="py-2 px-3">Last Used</th>
                  <th className="py-2 px-3">Created</th>
                  <th className="py-2 px-3">Status</th>
                  <th className="py-2 px-3 text-right">Action</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {tokens.map(t => (
                  <tr key={t.id} className={!t.is_active ? "opacity-60 bg-muted/20" : ""}>
                    <td className="py-2.5 px-3 font-medium text-foreground">{t.name}</td>
                    <td className="py-2.5 px-3 font-mono text-micro text-muted-foreground">{t.token_prefix}</td>
                    <td className="py-2.5 px-3">
                      <div className="flex gap-1 flex-wrap">
                        {t.scopes.map(s => (
                          <span
                            key={s}
                            className={`px-1.5 py-0.5 rounded text-micro uppercase font-medium ${
                              s === "write"
                                ? "bg-amber-100 text-amber-800 dark:bg-amber-900/50 dark:text-amber-300"
                                : "bg-muted text-muted-foreground"
                            }`}
                          >
                            {s}
                          </span>
                        ))}
                      </div>
                    </td>
                    <td className="py-2.5 px-3 text-micro text-muted-foreground">{formatDate(t.last_used_at)}</td>
                    <td className="py-2.5 px-3 text-micro text-muted-foreground">{formatDate(t.created_at)}</td>
                    <td className="py-2.5 px-3">
                      {t.is_active ? (
                        <span className="inline-flex items-center text-micro text-action font-medium">
                          Active
                        </span>
                      ) : (
                        <span className="inline-flex items-center text-micro text-destructive font-medium">
                          Revoked ({formatDate(t.revoked_at)})
                        </span>
                      )}
                    </td>
                    <td className="py-2.5 px-3 text-right">
                      {t.is_active ? (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleRevoke(t)}
                          className="h-7 px-2 text-destructive hover:bg-destructive/10 hover:text-destructive"
                          title="Revoke Token"
                          data-testid={`revoke-token-${t.id}`}
                        >
                          <Trash2 className="h-3.5 w-3.5 mr-1" />
                          Revoke
                        </Button>
                      ) : (
                        <span className="text-micro text-muted-foreground italic">Disabled</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </section>
  );
}
